import * as React from "react";
import { useState } from "react";
import {
  makeStyles,
  Text,
  Input,
  Label,
  Button,
  Link,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarIntent,
  MessageBarActions,
  Field,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";

const brandColor = "#921784";

// --- Styles ---
const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    padding: "20px",
    boxSizing: "border-box",
    gap: "20px",
  },
  title: {
    fontSize: "20px",
    fontWeight: "600",
    color: brandColor,
    marginBottom: "5px",
  },
  description: {
      fontSize: "14px",
      textAlign: "center",
      color: "#3b3a39",
      marginBottom: "15px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "300px",
    gap: "15px",
  },
  inputContainer: { 
  },
   field: {
      width: '100%',
  },
  buttonContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      marginTop: "10px",
  },
  backLink: {
      marginTop: "20px",
      fontSize: "14px",
  },
  messageBarContainer: {
      width: '100%',
      maxWidth: '300px',
      marginBottom: '15px',
  }
});

// --- Props Interface ---
export interface ForgotPasswordScreenProps {
  onNavigateBack: () => void; 
  onSubmit: (email: string) => Promise<void>;
  isLoading?: boolean;
  message?: { text: string; type: MessageBarIntent } | null; 
  clearMessage?: () => void;
}

// --- Component ForgotPasswordScreen ---
const ForgotPasswordScreen: React.FC<ForgotPasswordScreenProps> = ({
    onNavigateBack,
    onSubmit,
    isLoading,
    message,
    clearMessage,
}) => {
  const styles = useStyles();
  const [email, setEmail] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null); 

  const emailId = React.useId();

  const isEmailValid = (email: string): boolean => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setValidationError(null); 
    if (message && clearMessage) clearMessage();

    if (!isEmailValid(email)) {
      setValidationError("Por favor, ingresa un correo electrónico válido.");
      return;
    }

    if (!isLoading) {
      onSubmit(email);
    }
  };

  const handleEmailChange = (_e: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
    if (validationError) setValidationError(null);
    if (message && clearMessage) clearMessage();
    setEmail(data.value);
  };

  return (
    <div className={styles.container}>
      <Text as="h2" className={styles.title}>
        Recuperar Contraseña
      </Text>
      <Text className={styles.description}>
        Ingresa tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.
      </Text>

      {/* Show Messages */}
      {message && (
        <div className={styles.messageBarContainer}>
          <MessageBar
            intent={message.type}
          >
            <MessageBarBody>{message.text}</MessageBarBody>
            <MessageBarActions>
              <Button
                appearance="transparent"
                icon={<DismissRegular />}
                aria-label="Cerrar mensaje"
                onClick={clearMessage}
              />
            </MessageBarActions>
          </MessageBar>
        </div>
      )}

      <form onSubmit={handleFormSubmit} className={styles.form}>
        <Field
          required
          className={styles.field}
          validationState={validationError ? "error" : "none"}
          validationMessage={validationError}
        >
           <Label htmlFor={emailId} required>Correo Electrónico</Label>
           <Input
                type="email"
                id={emailId}
                value={email}
                onChange={handleEmailChange}
                required
                placeholder="tu.correo@ejemplo.com"
                disabled={isLoading}
            />
        </Field>


        <div className={styles.buttonContainer}>
          <Button
            appearance="primary"
            type="submit"
            disabled={isLoading}
            icon={isLoading ? <Spinner size="tiny" /> : undefined}
          >
            {isLoading ? "Enviando..." : "Enviar Instrucciones"}
          </Button>
        </div>
      </form>

      <Link onClick={onNavigateBack} className={styles.backLink} disabled={isLoading}>
        Volver a Inicio de Sesión
      </Link>
    </div>
  );
};

export default ForgotPasswordScreen;
